import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Plane, PlaneTakeoff, PlaneLanding, Copy, Mail, Pencil, Trash2,
  MessageCircle, ExternalLink, BellRing, ArrowRight, RefreshCw, Search,
} from "lucide-react";
import EmissionFormDialog from "@/components/EmissionFormDialog";
import { toast } from "sonner";
import { buildWaUrl, checkinMessage } from "@/lib/whatsapp";
import { DisplayName } from "@/lib/name";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const AIRLINES = ["LATAM", "GOL", "AZUL", "IBERIA", "TAP PORTUGAL"];

function fmtDT(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}
function formatBR(date) {
  if (!date) return "";
  const [y, m, d] = date.split("-");
  return `${d}/${m}/${y}`;
}
function maskCpf(cpf) {
  const v = (cpf || "").replace(/\D/g, "");
  if (v.length !== 11) return cpf;
  return `${v.slice(0,3)}.${v.slice(3,6)}.${v.slice(6,9)}-${v.slice(9,11)}`;
}
function hoursUntil(iso) {
  if (!iso) return Infinity;
  return (new Date(iso).getTime() - Date.now()) / 36e5;
}

async function copyToClipboard(text, successMsg = "Copiado") {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(successMsg);
  } catch {
    toast.error("Não foi possível copiar");
  }
}

function PassengerChip({ p, emission }) {
  const handleCopy = () =>
    copyToClipboard(
      `${p.full_name}\n${formatBR(p.birth_date)}\n${maskCpf(p.cpf)}`,
      `Dados de ${p.full_name.split(" ")[0]} copiados`,
    );
  const waUrl = buildWaUrl(p.phone, checkinMessage(p, emission));
  return (
    <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-100 rounded-lg px-2.5 py-1"
      data-testid={`passenger-chip-${emission.emission_id}-${p.client_id}`}>
      <span className="text-sm text-slate-800 font-medium"><DisplayName name={p.full_name} /></span>
      <button type="button" onClick={handleCopy} title="Copiar nome, nascimento e CPF"
        className="h-6 w-6 grid place-items-center rounded hover:bg-white text-[#0b3a82]"
        data-testid={`passenger-copy-${emission.emission_id}-${p.client_id}`}>
        <Copy className="w-3.5 h-3.5" />
      </button>
      {p.phone && (
        <a href={waUrl} target="_blank" rel="noreferrer"
          title="Enviar lembrete pelo WhatsApp"
          className="h-6 w-6 grid place-items-center rounded hover:bg-white text-emerald-600"
          data-testid={`passenger-wa-${emission.emission_id}-${p.client_id}`}>
          <MessageCircle className="w-3.5 h-3.5" />
        </a>
      )}
    </div>
  );
}

function ConnectionBlock({ kind, conn, emission }) {
  return (
    <div className="text-xs bg-blue-50/60 border border-blue-100 rounded-lg p-2.5"
      data-testid={`${kind}-connection-${emission.emission_id}`}>
      <div className="font-semibold text-[#0b3a82] flex items-center gap-1">
        <RefreshCw className="w-3.5 h-3.5" />
        Conexão {kind === "outbound" ? "ida" : "volta"} — {conn.airport || conn.origin}
      </div>
      <div className="text-slate-600 mt-0.5">
        {conn.origin} <ArrowRight className="inline w-3 h-3 mx-0.5" /> {conn.destination}
        {conn.flight_number && <> · Voo {conn.flight_number}</>}
        {conn.pnr && <> · Loc. {conn.pnr}</>}
      </div>
      <div className="text-slate-500 mt-0.5">{fmtDT(conn.datetime_at)}</div>
    </div>
  );
}

function EmissionCard({ e, onEditClick, onDelete, onCheckinEmail, onConfirmEmail, onAgentAlert }) {
  const hu = hoursUntil(e.departure_at);
  const checkinSoon = hu > 0 && hu <= 48;
  const passengers = e.passengers || [];
  return (
    <div className="soft-card p-5" data-testid={`emission-card-${e.emission_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-slate-500 font-medium">
            {passengers.length} passageiro(s)
          </div>
          <div className="mt-1 text-xl font-bold display text-slate-900 flex items-center gap-2 truncate">
            {e.origin} <ArrowRight className="w-4 h-4 text-blue-500" /> {e.destination}
            {e.return_at && <RefreshCw className="w-4 h-4 text-slate-400" title="Ida e volta" />}
          </div>
          <div className="text-xs text-slate-500 mt-1 truncate flex items-center gap-1.5 flex-wrap">
            <span className="font-medium text-slate-700">{e.airline}</span>
            <span className="text-slate-300">·</span>
            <span>Localizador:</span>
            <b className="text-slate-800">{e.pnr}</b>
            <button type="button" onClick={() => copyToClipboard(e.pnr, "Localizador copiado")}
              className="h-5 w-5 grid place-items-center rounded hover:bg-slate-100 text-[#0b3a82]"
              title="Copiar localizador"
              data-testid={`copy-pnr-${e.emission_id}`}>
              <Copy className="w-3 h-3" />
            </button>
            {e.flight_number && (
              <>
                <span className="text-slate-300">·</span>
                <span>Voo {e.flight_number}</span>
              </>
            )}
          </div>
        </div>
        {(e.ticket_url || e.ticket_url_return) && (
          <div className="flex items-center gap-1 shrink-0">
            {e.ticket_url && (
              <a href={e.ticket_url} target="_blank" rel="noreferrer"
                className="text-[11px] font-semibold text-[#0b3a82] hover:bg-blue-50 rounded-md px-2 py-1 border border-blue-200 flex items-center gap-1"
                title="Passagem da ida" data-testid={`ticket-link-${e.emission_id}`}>
                <ExternalLink className="w-3 h-3" /> Ida
              </a>
            )}
            {e.ticket_url_return && (
              <a href={e.ticket_url_return} target="_blank" rel="noreferrer"
                className="text-[11px] font-semibold text-orange-700 hover:bg-orange-50 rounded-md px-2 py-1 border border-orange-200 flex items-center gap-1"
                title="Passagem da volta" data-testid={`ticket-link-return-${e.emission_id}`}>
                <ExternalLink className="w-3 h-3" /> Volta
              </a>
            )}
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {passengers.map((p) => <PassengerChip key={p.client_id} p={p} emission={e} />)}
      </div>

      <div className="mt-3 text-sm text-slate-700 grid grid-cols-1 gap-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <PlaneTakeoff className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="font-medium">{fmtDT(e.departure_at)}</span>
          {e.flight_number && <span className="text-xs text-slate-500">Voo {e.flight_number}</span>}
        </div>
        {e.return_at && (
          <div className="flex items-center gap-2 flex-wrap" data-testid={`return-info-${e.emission_id}`}>
            <PlaneLanding className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="font-medium">{fmtDT(e.return_at)}</span>
            {(e.return_origin || e.return_destination) && (
              <span className="text-xs text-slate-600">
                {e.return_origin || e.destination}
                <ArrowRight className="inline w-3 h-3 mx-0.5" />
                {e.return_destination || e.origin}
              </span>
            )}
            {e.return_airline && e.return_airline !== e.airline && (
              <span className="text-xs text-slate-500">{e.return_airline}</span>
            )}
            {e.return_flight_number && (
              <span className="text-xs text-slate-500">Voo {e.return_flight_number}</span>
            )}
            {e.return_pnr && e.return_pnr !== e.pnr && (
              <span className="text-xs text-slate-600 inline-flex items-center gap-1">
                Loc. <b className="text-slate-800">{e.return_pnr}</b>
                <button type="button" onClick={() => copyToClipboard(e.return_pnr, "Localizador da volta copiado")}
                  className="h-5 w-5 grid place-items-center rounded hover:bg-slate-100 text-[#0b3a82]"
                  title="Copiar localizador da volta"
                  data-testid={`copy-return-pnr-${e.emission_id}`}>
                  <Copy className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {(e.outbound_connection || e.return_connection) && (
        <div className="mt-3 space-y-2">
          {e.outbound_connection && <ConnectionBlock kind="outbound" conn={e.outbound_connection} emission={e} />}
          {e.return_connection && <ConnectionBlock kind="return" conn={e.return_connection} emission={e} />}
        </div>
      )}

      {checkinSoon && (
        <div className="mt-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs flex items-center gap-2"
          data-testid={`checkin-alert-${e.emission_id}`}>
          <BellRing className="w-3.5 h-3.5" />
          Check-in liberado! Partida em {Math.round(hu)}h
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="border-blue-200 text-[#0b3a82] hover:bg-blue-50"
          onClick={() => onConfirmEmail(e)}
          data-testid={`confirmation-email-${e.emission_id}`}>
          <Mail className="w-3.5 h-3.5 mr-1" /> Enviar passagem
        </Button>
        <Button size="sm" variant="outline" className="border-blue-200 text-[#0b3a82] hover:bg-blue-50"
          onClick={() => onCheckinEmail(e)}
          data-testid={`checkin-email-${e.emission_id}`}>
          <BellRing className="w-3.5 h-3.5 mr-1" /> Lembrete check-in
        </Button>
        <Button size="sm" variant="outline" className="border-orange-200 text-orange-700 hover:bg-orange-50"
          onClick={() => onAgentAlert(e)}
          title="Enviar para o seu próprio email"
          data-testid={`agent-alert-${e.emission_id}`}>
          <Mail className="w-3.5 h-3.5 mr-1" /> Avisar a mim
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onEditClick(e)}
          data-testid={`edit-emission-${e.emission_id}`}>
          <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
        </Button>
        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={() => onDelete(e)}
          data-testid={`delete-emission-${e.emission_id}`}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Excluir
        </Button>
      </div>
    </div>
  );
}

export default function EmissionsTab({ onChange, autoOpenSignal = 0, initialSearch = "" }) {
  const [filter, setFilter] = useState("upcoming");
  const [airline, setAirline] = useState("all");
  const [search, setSearch] = useState(initialSearch);
  const [items, setItems] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (autoOpenSignal > 0) {
      setEditing(null);
      setOpenForm(true);
    }
  }, [autoOpenSignal]);

  useEffect(() => {
    if (initialSearch) setSearch(initialSearch);
  }, [initialSearch]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { filter };
      if (airline !== "all") params.airline = airline;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/emissions", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => { load(); }, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [filter, airline, search]);

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/emissions/${deleting.emission_id}`);
      toast.success("Emissão excluída");
      setDeleting(null);
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleCheckinEmail = async (em) => {
    try {
      const { data } = await api.post(`/emissions/${em.emission_id}/checkin-email`);
      if (data.status === "sandbox_blocked") {
        toast.warning(`Modo sandbox do Resend ativo: email para ${data.to || "cliente"} bloqueado. Verifique um domínio no Resend para liberar.`, { duration: 7000 });
      } else {
        toast.success(`Lembrete enviado para ${data.to}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleConfirmEmail = async (em) => {
    try {
      const { data } = await api.post(`/emissions/${em.emission_id}/confirmation-email`);
      if (data.status === "sandbox_blocked") {
        toast.warning(`Modo sandbox do Resend ativo: email para ${data.to || "cliente"} bloqueado. Verifique um domínio no Resend para liberar.`, { duration: 7000 });
      } else {
        toast.success(`Passagem enviada para ${data.to}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleAgentAlert = async (em) => {
    try {
      const { data } = await api.post(`/emissions/${em.emission_id}/agent-checkin-alert`);
      if (data.status === "sandbox_blocked") {
        toast.warning(data.message || "Email bloqueado pelo Resend.");
      } else {
        toast.success(`Alerta enviado para ${data.to}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="emissions-tab">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold display text-slate-900 flex items-center gap-2">
            <Plane className="w-5 h-5 text-[#0b3a82]" /> Emissões
          </h2>
          <p className="text-slate-500 text-sm">Gerencie todas as suas emissões aéreas.</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpenForm(true); }}
          className="h-11 orange-grad text-white hover:opacity-95"
          data-testid="add-emission-button">
          <Plus className="w-4 h-4 mr-1" /> Nova emissão
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <Tabs value={filter} onValueChange={setFilter} className="flex-1">
          <TabsList className="bg-white border border-slate-200" data-testid="emissions-filter-tabs">
            <TabsTrigger value="upcoming" data-testid="filter-upcoming">Próximas</TabsTrigger>
            <TabsTrigger value="past" data-testid="filter-past">Passadas</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome do cliente"
            className="pl-9 bg-white"
            data-testid="emissions-search"
          />
        </div>
        <Select value={airline} onValueChange={setAirline}>
          <SelectTrigger className="w-[180px] bg-white" data-testid="airline-filter">
            <SelectValue placeholder="Companhia" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as cias</SelectItem>
            {AIRLINES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-slate-500 text-center py-12" data-testid="emissions-loading">Carregando...</div>
      ) : items.length === 0 ? (
        <div className="soft-card p-10 text-center" data-testid="emissions-empty">
          <Plane className="w-12 h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.4} />
          <div className="text-lg font-semibold text-slate-900">Nada por aqui ainda</div>
          <p className="text-slate-500 mt-1">As emissões aparecem nesta aba.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((e) => (
            <EmissionCard key={e.emission_id} e={e}
              onEditClick={(em) => { setEditing(em); setOpenForm(true); }}
              onDelete={(em) => setDeleting(em)}
              onCheckinEmail={handleCheckinEmail}
              onConfirmEmail={handleConfirmEmail}
              onAgentAlert={handleAgentAlert}
            />
          ))}
        </div>
      )}

      <EmissionFormDialog
        open={openForm}
        onOpenChange={setOpenForm}
        emission={editing}
        airlines={AIRLINES}
        onSaved={() => { setOpenForm(false); load(); onChange?.(); }}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-emission-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir emissão?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700"
              data-testid="delete-emission-confirm">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
